import { useMediaQuery } from '@mui/material';

import Icon from 'atoms/Icons/Icon';
import { media } from 'atoms/breakpoints/breakpoints';
import color from 'atoms/colors/colors';

import Flex from 'quarks/Flex';
import Text from 'quarks/Text';
import type { FlexProps } from 'quarks/interpolations/flex';

import Button from 'molecules/Button/Button';
import getPaginationSpread from 'molecules/Pagination/utils/getPaginationSpread';

import type { FC } from 'react';

interface PaginationProps extends FlexProps {
  currentPage: number;
  setCurrentPage: (e: number) => void;
  totalPages: number;
  prefix?: string;
  isSearch?: boolean;
}

const Pagination: FC<PaginationProps> = ({ currentPage, setCurrentPage, totalPages, prefix, isSearch, ...props }) => {
  const isTablet = useMediaQuery(media.md);
  const pageArray = [...new Array(totalPages + 1).keys()];
  pageArray.shift();

  const pagination = totalPages > 8 ? getPaginationSpread(pageArray, currentPage, totalPages) : pageArray;

  const handleClick = (page: number | string, idx: number) => {
    const spreadLink = idx + 1 >= currentPage ? currentPage + 2 : currentPage - 2;
    const targetPage = typeof page === 'number' ? page : spreadLink;

    setCurrentPage(targetPage);
  };

  return (
    <Flex
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      gap="16px"
      height="fit-content"
      width="100%"
      {...props}
    >
      <Button
        size="md"
        boxShadow="unset"
        minWidth="40px"
        maxWidth="40px"
        height="40px"
        borderRadius="8px"
        paddingAll={8}
        disabled={currentPage === 1}
        cursor={currentPage > 1 ? 'pointer' : 'default'}
        backgroundColor={currentPage > 1 ? 'green-50' : 'green-50'}
        onClick={() => setCurrentPage(currentPage - 1)}
        data-cy="leftArrow"
        css={`
          &.MuiButtonBase-root {
            width: fit-content !important;
          }
        `}
      >
        <Icon id="arrow-left" fill={currentPage > 1 ? color.green[900] : color.green[300]} size={20} />
      </Button>
      {isTablet ? (
        <Flex flexDirection="row" gap="4px">
          {pagination.map((page, idx) => {
            const isActive = currentPage === page;
            const isNextDot = idx > 3;
            const notCenterDot = isNextDot ? Number(pagination[3]) + 1 : Number(pagination[3]) - 1;
            const dotPath = idx === 3 ? 4 : notCenterDot;

            return (
              <Button
                // eslint-disable-next-line react/no-array-index-key
                key={idx}
                href={
                  isSearch
                    ? undefined
                    : `${prefix}/${page === 1 ? '' : `page/${page === '...' ? dotPath : page}`}#listing-section`
                }
                alignItems="center"
                justifyContent="center"
                minWidth="40px"
                maxWidth="40px"
                height="40px"
                borderRadius="8px"
                fontWeight="regular"
                boxShadow="none"
                transition="color 100ms ease, background-color 150ms ease-in-out"
                aria-label={`Pagination link to page number ${page === '...' ? dotPath : page}`}
                textColor={isActive ? 'gray-900' : 'gray-700'}
                backgroundColor={isActive ? 'green-300' : undefined}
                onClick={() => handleClick(page, idx)}
                data-cy={page}
                css={`
                  &.MuiButton-root {
                    max-width: 40px !important;
                  }
                `}
              >
                {page}
              </Button>
            );
          })}
        </Flex>
      ) : (
        <Text style={{ color: color.common.black }} textStyle="textSm">{`Page ${currentPage} of ${totalPages}`}</Text>
      )}
      <Button
        size="md"
        boxShadow="unset"
        minWidth="40px"
        maxWidth="40px"
        height="40px"
        borderRadius="8px"
        paddingAll={8}
        disabled={currentPage === totalPages}
        cursor={currentPage < totalPages ? 'pointer' : 'default'}
        backgroundColor={currentPage < totalPages ? 'green-50' : 'green-50'}
        onClick={() => setCurrentPage(currentPage + 1)}
        data-cy="rightArrow"
        css={`
          &.MuiButtonBase-root {
            width: fit-content !important;
          }
        `}
      >
        <Icon id="arrow-right" fill={currentPage < totalPages ? color.green[900] : color.green[300]} size={20} />
      </Button>
    </Flex>
  );
};

export default Pagination;
